import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useForm, useWatch } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";

import { useFormAutoSave } from "hooks";
import type { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import WordProcessorField from "components/word-processor-field";
import {
  emptyWordProcessorValue,
  isWordProcessorHtmlEqual
} from "components/word-processor-field/word-processor-value.utils";
import { setAiPromptDetails } from "components/word-processor-field/word-processor.slice";
import { PROMPT_TYPE_SCRIBE_CONTEXT } from "constants/prompts";
import { selectCurrentClinic } from "features/auth/auth-slice";
import { getScribeModalNestedDialogZIndex } from "features/medical-scribe/medical-scribe-constants";
import ScribeCustomContextInfoBanner from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/scribe-custom-context-info-banner.component";
import {
  useAskAiOnScribeContextMutation,
  useUpdateAmbientScribeMutation
} from "services/ambient-scribe";

import { CONTEXT_SAVE_FLUSH_TIMEOUT_MS } from "./paused-scribe-actions.util";
import { isCustomContextEmpty } from "./scribe-context.util";

type ContextForm = {
  custom_context: string;
};

type ScribeContextEditorProps = {
  scribeId: number;
  customContext?: string | null;
  patientId?: number;
  userId?: number;
  isActive: boolean;
  onFlushSaveRef?: MutableRefObject<(() => Promise<void>) | null>;
};

export default function ScribeContextEditor({
  scribeId,
  customContext,
  patientId,
  userId,
  isActive,
  onFlushSaveRef
}: Readonly<ScribeContextEditorProps>) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const clinic_id = currentClinic?.id ? Number(currentClinic.id) : undefined;

  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorInstanceRef = useRef<Editor | null>(null);
  const [editorHeight, setEditorHeight] = useState(300);

  const [updateScribe, { isLoading: isManualSaving }] =
    useUpdateAmbientScribeMutation();
  const [askAiOnContext] = useAskAiOnScribeContextMutation();

  const { control, reset, formState } = useForm<ContextForm>({
    defaultValues: { custom_context: "" }
  });

  const [currentSavedData, setCurrentSavedData] = useState<ContextForm>({
    custom_context: ""
  });

  const customContextValue = useWatch({ control, name: "custom_context" });
  const showContextBanner = isCustomContextEmpty(customContextValue);

  useEffect(() => {
    if (!scribeId) return;
    if (!formState.isDirty) {
      const values = { custom_context: customContext ?? "" };
      reset(values);
      setCurrentSavedData(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scribeId]);

  const postSave = useCallback(
    (data: ContextForm) => {
      setCurrentSavedData(data);
      reset(data);
    },
    [reset]
  );

  const skipUpdateComparator = useCallback(
    (newData: ContextForm) =>
      isWordProcessorHtmlEqual(
        currentSavedData.custom_context,
        newData.custom_context
      ),
    [currentSavedData]
  );

  const onSubmit = useCallback(
    (data: ContextForm) =>
      updateScribe({
        id: scribeId,
        custom_context: data.custom_context || emptyWordProcessorValue,
        // Detail-only cache patch; scribe list updates on explicit user actions.
        skipCacheInvalidation: true
      }),
    [scribeId, updateScribe]
  );

  const { dataSaveStateContent, isUpdating } = useFormAutoSave({
    control,
    delay: 3000,
    updateData: onSubmit,
    reset: postSave,
    skipUpdateComparator,
    groupKey: `scribe-context-${scribeId}`,
    dataDogLabel: "Scribe Context"
  });

  const isUpdatingRef = useRef(isUpdating);
  isUpdatingRef.current = isUpdating;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const waitForSaveIdle = useCallback(
    () =>
      new Promise<void>((resolve) => {
        if (!isUpdatingRef.current) {
          resolve();
          return;
        }
        const deadline = Date.now() + CONTEXT_SAVE_FLUSH_TIMEOUT_MS;
        const intervalId = window.setInterval(() => {
          if (!mountedRef.current) {
            window.clearInterval(intervalId);
            resolve();
            return;
          }
          if (!isUpdatingRef.current || Date.now() >= deadline) {
            window.clearInterval(intervalId);
            resolve();
          }
        }, 50);
      }),
    []
  );

  const saveWithCacheInvalidation = useCallback(async () => {
    if (!formState.isValid) return;

    await waitForSaveIdle();

    const data: ContextForm = { custom_context: customContextValue ?? "" };
    if (skipUpdateComparator(data)) return;

    try {
      await updateScribe({
        id: scribeId,
        custom_context: data.custom_context || emptyWordProcessorValue,
        skipCacheInvalidation: false
      }).unwrap();
      postSave(data);
    } catch {
      // no invalidation — mutation invalidatesTags returns [] on error
    }
  }, [
    formState.isValid,
    waitForSaveIdle,
    customContextValue,
    skipUpdateComparator,
    updateScribe,
    scribeId,
    postSave
  ]);

  useEffect(() => {
    if (onFlushSaveRef) {
      onFlushSaveRef.current = saveWithCacheInvalidation;
    }
    return () => {
      if (onFlushSaveRef) {
        onFlushSaveRef.current = null;
      }
    };
  }, [saveWithCacheInvalidation, onFlushSaveRef]);

  const resizeTinyMceEditor = useCallback((height: number) => {
    const editor = editorInstanceRef.current;
    if (!editor || editor.destroyed) return;

    const container = editor.getContainer();
    if (container) {
      container.style.height = `${height}px`;
    }
    editor.dispatch("ResizeEditor");
  }, []);

  const handleEditorAfterInit = useCallback(
    (editor: Editor) => {
      editorInstanceRef.current = editor;
      resizeTinyMceEditor(editorHeight);
    },
    [editorHeight, resizeTinyMceEditor]
  );

  useEffect(() => {
    if (!isActive) return;
    const el = editorContainerRef.current;
    if (!el) return;

    const updateHeight = () => {
      requestAnimationFrame(() => {
        const next = Math.floor(el.getBoundingClientRect().height);
        if (next > 0) {
          setEditorHeight(next);
        }
      });
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    resizeTinyMceEditor(editorHeight);
  }, [isActive, editorHeight, resizeTinyMceEditor]);

  const handleAskAi = useCallback(
    ({
      id,
      prompt,
      fieldContent,
      lastResult
    }: {
      id: string;
      prompt: string;
      fieldContent: string;
      lastResult?: string;
    }) => {
      dispatch(
        setAiPromptDetails({
          id,
          prompt,
          result: lastResult,
          dialogOpen: true,
          isBusy: true
        })
      );
      return askAiOnContext({
        scribe_id: scribeId,
        text: fieldContent,
        user_prompt: prompt,
        clinic_id
      }).then(({ data }: { data?: string }) => {
        dispatch(
          setAiPromptDetails({
            id,
            result: data,
            isBusy: false,
            dialogOpen: true
          })
        );
      });
    },
    [scribeId, clinic_id, dispatch, askAiOnContext]
  );

  return (
    <>
      {showContextBanner && <ScribeCustomContextInfoBanner userId={userId} />}
      <Box
        ref={editorContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          minWidth: 0,
          overflow: "hidden"
        }}
      >
        <WordProcessorField
          name="custom_context"
          control={control}
          placeholder="Add context that will be included in Scribe note generation…"
          mergeTagsPatientId={patientId}
          onAskAi={handleAskAi}
          askAiPromptType={PROMPT_TYPE_SCRIBE_CONTEXT}
          afterInitCallback={handleEditorAfterInit}
          height={editorHeight}
          sx={{ flex: 1, width: "100%", height: "100%" }}
          featuresGroup="light"
          allowMergeTags
          allowInsertMergeTagsValues
          nestedDialogZIndex={getScribeModalNestedDialogZIndex(
            theme.zIndex.snackbar
          )}
        />
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 0.5,
          p: 1,
          pr: 1
        }}
      >
        {dataSaveStateContent}
        <LoadingButton
          loading={isUpdating || isManualSaving}
          disabled={isUpdating || isManualSaving}
          onClick={() => saveWithCacheInvalidation()}
          variant="contained"
          size="small"
          sx={{ minWidth: "auto" }}
        >
          Save
        </LoadingButton>
      </Box>
    </>
  );
}
